// src/app/utility/i18n/language-switcher.component.ts
// ════════════════════════════════════════════════════════════════════════════
// DE-6 — the language switcher.
//
// It shows each language in its OWN script — हिन्दी, తెలుగు, मराठी — not "Hindi", "Telugu",
// "Marathi". A person who cannot read English cannot find English words for their own language in a
// dropdown, and they are precisely the person this control exists for.
//
// Lives in UtilityModule, so it drops into the app shell, the login page and (DE-7) the public
// survey page alike.
// ════════════════════════════════════════════════════════════════════════════
import { Component, Input } from '@angular/core';
import { I18nService, LanguageRow } from '../../services/i18n.service';

@Component({
  selector: 'app-language-switcher',
  template: `
    <div class="lang-switch" *ngIf="languages.length > 1">
      <i class="fa fa-globe" *ngIf="showIcon"></i>

      <button type="button"
              class="lang-opt"
              *ngFor="let l of languages"
              [class.is-on]="l.code === current"
              [disabled]="switching"
              (click)="pick(l)"
              [title]="l.name">
        {{ compact ? shortOf(l) : l.native_name }}
      </button>
    </div>
  `,
  styles: [`
    .lang-switch {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      border: 1px solid #e5e7eb;
      border-radius: 20px;
      background: #fff;
      padding: 2px;
    }
    .fa-globe { color: #9ca3af; font-size: 12px; margin: 0 5px 0 7px; }
    .lang-opt {
      border: 0;
      background: transparent;
      color: #6b7280;
      font-size: 12px;
      font-weight: 600;
      border-radius: 20px;
      padding: 4px 11px;
      cursor: pointer;
      white-space: nowrap;
    }
    .lang-opt:hover:not(:disabled) { background: #f3f4f6; }
    .lang-opt.is-on { background: #2563EB; color: #fff; }
    .lang-opt:disabled { opacity: .6; cursor: default; }
  `],
})
export class LanguageSwitcherComponent {

  /** Compact = two letters (EN / हि). Use in a cramped topbar. */
  @Input() compact = false;
  @Input() showIcon = true;

  /**
   * Narrow the choices to what the surrounding content can actually deliver.
   *
   * Empty (the default) keeps the old behaviour — every language the app knows.
   * A public survey passes the languages its own template is translated into,
   * because offering Hindi on a form that only exists in English and Telugu is
   * an offer that cannot be kept. The original language is always kept in the
   * list: it is what the form was authored in, so it is always available.
   */
  @Input() limitTo: string[] = [];

  switching = false;

  constructor(private i18n: I18nService) {}

  get languages(): LanguageRow[] {
    const all = this.i18n.languages || [];
    if (!this.limitTo || !this.limitTo.length) return all;
    const want = new Set(this.limitTo.map(x => String(x).toLowerCase()));
    // `en` stays whatever the caller passed: a translated form can always be
    // read in the language it was written in.
    want.add('en');
    return all.filter(l => want.has(String(l.code).toLowerCase()));
  }
  get current(): string { return this.i18n.lang; }

  shortOf(l: LanguageRow): string {
    // First character of the NATIVE name — "हि", "తె", "मर" — not "HI"/"TE", which mean nothing to
    // someone who does not read Latin script.
    return (l.native_name || l.code).slice(0, 2);
  }

  async pick(l: LanguageRow): Promise<void> {
    if (l.code === this.current || this.switching) return;
    this.switching = true;
    try {
      await this.i18n.use(l.code);
    } finally {
      this.switching = false;
    }
  }
}
